// DebriefJobs.js
import React, { useEffect, useState } from 'react';
import { Table, message, Spin, Select, Input, Button, Popconfirm, Modal, Form, DatePicker, InputNumber } from 'antd';
import { IconBuildingFactory2, IconCloudRain, IconGrain, IconSnowflake, IconBriefcase, IconCloudQuestion } from '@tabler/icons-react';
import { SearchOutlined, DeleteOutlined, EditOutlined, PlusOutlined, SyncOutlined} from '@ant-design/icons';
import { IconTractor, IconUser } from '@tabler/icons-react';
import api from "../../../authentication/axiosInstance";
import { Box } from '@mui/material';
import SyncJobsModal from '../../../components/SyncJobsModal';

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);

const { Option } = Select;

const DebriefJobs = () => {

  const [jobs, setJobs] = useState([]);
  const [jobTypeFilter, setJobTypeFilter] = useState("All");
  const [stormTypeFilter, setStormTypeFilter] = useState("");
  const [siteNameFilters, setSiteNameFilters] = useState([]);
  const [staffNameFilter, setStaffNameFilter] = useState("");
  const [vehicleNameFilter, setVehicleNameFilter] = useState("");

  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [editForm] = Form.useForm();

  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [addForm] = Form.useForm();   

  const [jobTypes, setJobTypes] = useState([]);
  const [storms, setStorms] = useState([]);
  const [sites, setSites] = useState([]);
  const [staff, setStaff] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0
  });

  const [currentJobType, setCurrentJobType] = useState("");

  const formatDuration = (totalSeconds) => {
    if (totalSeconds === null || totalSeconds === undefined) {
      return 'N/A';
    }
  
    const isNegative = totalSeconds < 0;
    const seconds = Math.abs(Math.floor(totalSeconds));
  
    const days = Math.floor(seconds / (3600 * 24));
    const hours = Math.floor((seconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    // For exact minute accuracy, round up minute if seconds are 30 or more
    const roundedMinutes = minutes + (seconds % 60 >= 30 ? 1 : 0);
  
    const parts = [];
    if (days > 0) {
      parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    }
    if (hours > 0) {
      parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
    }
    if (roundedMinutes > 0 || (days === 0 && hours === 0)) {
      // Always show minutes if there are no days and hours
      parts.push(`${roundedMinutes} minute${roundedMinutes !== 1 ? 's' : ''}`);
    }
  
    const formattedDuration = parts.join(', ');
    return isNegative ? `- ${formattedDuration}` : formattedDuration;
  };

  const fetchJobs = async (page = 1, pageSize = 20) => {

    setLoading(true);
    try {
      // Create an array of params objects, one for each site if multiple sites are selected
      if (siteNameFilters.length > 1) {
        // Use Promise.all to make multiple requests, one for each site
        const responses = await Promise.all(
          siteNameFilters.map(site => 
            api.get('/debrief/jobs/', {
              params: {
                jobType: jobTypeFilter !== 'All' ? jobTypeFilter : undefined,
                staff: staffNameFilter || undefined,
                site: site,
                vehicle: vehicleNameFilter || undefined,
                stormType: stormTypeFilter !== 'All' ? stormTypeFilter : undefined,
                page: 1,
                pageSize: 1000 // Request more items per site to ensure we get all possible matches
              }
            })
          )
        );

        // Combine all job results and remove duplicates
        let allJobs = [];
        responses.forEach(response => {
          allJobs = [...allJobs, ...response.data.data];
        });

        // Remove duplicate jobs by job_id
        const uniqueJobs = allJobs.reduce((acc, job) => {
          if (!acc.find(j => j.job_id === job.job_id)) {
            acc.push(job);
          }
          return acc;
        }, []);

        // Apply pagination manually
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedJobs = uniqueJobs.slice(startIndex, endIndex);
        
        setJobs(paginatedJobs);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: uniqueJobs.length,
          totalPages: Math.ceil(uniqueJobs.length / pageSize)
        });
      } else {
        // If only one or no site is selected, use the regular API call
        await api.get('/debrief/jobs/', {
          params: {
            jobType: jobTypeFilter !== 'All' ? jobTypeFilter : undefined,
            staff: staffNameFilter || undefined,
            site: siteNameFilters.length === 1 ? siteNameFilters[0] : undefined,
            vehicle: vehicleNameFilter || undefined,
            stormType: stormTypeFilter !== 'All' ? stormTypeFilter : undefined,
            page: page,
            pageSize: pageSize
          }
        }).then((response) => {
          setJobs(response.data.data);
          setPagination({
            current: response.data.page,
            pageSize: response.data.pageSize,
            total: response.data.total,
            totalPages: response.data.totalPages
          });
        });
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      message.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchJobTypes = async () => {
    try {
      // For job types, we'll make a request without site filter if multiple sites are selected
      // This ensures we get all possible job types across selected sites
      if (siteNameFilters.length > 1) {
        await api.get('/debrief/jobs/types/', {
          params: {
            staff: staffNameFilter || undefined,
            vehicle: vehicleNameFilter || undefined,
            stormType: stormTypeFilter !== 'All' ? stormTypeFilter : undefined,
          }
        }).then((response) => {
          setJobTypes(response.data);
        });
      } else {
        await api.get('/debrief/jobs/types/', {
          params: {
            staff: staffNameFilter || undefined,
            site: siteNameFilters.length === 1 ? siteNameFilters[0] : undefined,
            vehicle: vehicleNameFilter || undefined,
            stormType: stormTypeFilter !== 'All' ? stormTypeFilter : undefined,
          }
        }).then((response) => {
          setJobTypes(response.data);
        });
      }
    } catch (error) {
      console.error('Error fetching jobs types:', error);
      message.error('Failed to fetch jobs types');
    }
  }

  useEffect(() => {
    fetchJobs(pagination.current, pagination.pageSize);
    fetchJobTypes()
  },[jobTypeFilter,staffNameFilter,siteNameFilters,vehicleNameFilter,stormTypeFilter])

  const capitalize = (text) => {
    if (!text) return "N/A"; // Return "N/A" if text is null or undefined
    return text
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word
  };

  const fetchOptions = async () => {
    try {
      const [stormsResponse, sitesResponse, staffResponse, vehiclesResponse] = await Promise.all([
        api.get('/dispatch/storm/'),
        api.get('/sites/'),
        api.get('/staff/'),
        api.get('/vehicles/')
      ]);
  
      setStorms(stormsResponse.data);
      setSites(sitesResponse.data);
      setStaff(staffResponse.data);
      setVehicles(vehiclesResponse.data);
    } catch (error) {
      console.error("Failed to fetch options:", error);
      message.error("Failed to load options for editing.");
    }
  };
  
  useEffect(() => {
    fetchOptions();
  }, []);
  
  const stormSeverityColors = [
    { severity: "light", color: "#034832" , bg: "#E5F2EC"},
    { severity: "medium", color: "#FFC107", bg: "#FFF8E1" },
    { severity: "heavy", color: "#FF4D4E", bg: "#FDEAEA" },
    { severity: "N/A", color: "#808080", bg: "#F5F5F5" },
  ]

  const getSeverityColor = (severity) => {
    const severityColor = stormSeverityColors.find(item => item.severity === severity);
    return severityColor ? severityColor : { color: "#000000", bg: "#FFFFFF" }; // Default fallback
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    return dayjs(dateTime).tz('America/New_York').format('MM/DD/YYYY hh:mm A');
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return dayjs(date).tz('America/New_York').format('MM/DD/YYYY');
  };

  const formatTime = (time) => {
    if (!time) return 'N/A';
    return dayjs(time).tz('America/New_York').format('hh:mm A');
  };

  const showAddModal = () => {
    setIsAddModalVisible(true);
  };
  
  const handleAddCancel = () => {
    setIsAddModalVisible(false);
    addForm.resetFields();
  };

  const handleAddSubmit = async (values) => {
    try {
      const newJob = {
        storm: values.storm_id || null,
        site: values.site_id || null,
        staff: values.staff_ids || [],  // Send array of staff IDs
        vehicle: values.vehicle_id || null,
        type: values.type,
        job_start_date: values.job_start_date ? values.job_start_date.tz('UTC').format() : null,
        job_end_date: values.job_end_date ? values.job_end_date.tz('UTC').format() : null,
      };
  
      await api.post('/debrief/jobs/', newJob);
      message.success('Job added successfully');
      setIsAddModalVisible(false);
      fetchJobs(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error('Failed to add job:', error);
      message.error('Failed to add job');
    }
  };

  const handleSyncModalClose = async () => {
    fetchJobs();
    setIsSyncModalOpen(false);
  };

  const columns = [
    {
      title: 'Job Details',
      render: (text, record) => {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              flexDirection: 'row',
              gap: '10px',
            }}
          >
            {/* Briefcase Icon */}
            <div
              style={{
                height: '30px',
                width: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f0f2f5',
                borderRadius: '4px',
              }}
            >
              <IconBriefcase size={18} stroke={2} />
            </div>
    
            {/* Time Details */}
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                flexDirection: 'column',
              }}
            >
              {/* Job Type */}
              <span style={{ fontWeight: '600', fontSize: '16px' }}>
                {record.type || 'N/A'}
              </span>
    
              {/* Job Time Period */}
              <span style={{ fontSize: '14px', marginTop: '2px' }}>
                Date: {formatDate(record.job_start_date)}
              </span>
              
              {/* Start and End Time */}
              <span style={{ fontSize: '14px', marginTop: '2px' }}>
                Time: {formatTime(record.job_start_date)} - {formatTime(record.job_end_date)}
              </span>
    
              {/* Job Date Created */}
              <span style={{ fontSize: '14px', marginTop: '2px' }}>
                Created: {formatDate(record.job_date_created)}
              </span>
    
              {/* Crew Size */}
              <span style={{ fontSize: '14px', marginTop: '2px' }}>
                Crew Size: {record.crew_size || 'N/A'}
              </span>

              {/* Man Hours */}
              <span style={{ fontSize: '14px', marginTop: '2px' }}>
                Man Hours: {record.man_hours ? `${record.man_hours.toFixed(1)}h` : 'N/A'}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Duration',
      width: 180,
      render: (text, record) => {
        return (
          <div 
            style={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#f6f8fa',
              padding: '10px',
              borderRadius: '4px',
              minHeight: '80px',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
            }}
          >
            <span style={{ fontSize: '15px', fontWeight: '500' }}>
              {record.duration ? formatDuration(record.duration) : 'N/A'}
            </span>
          </div>
        );
      }
    },
    {
      title: 'Storm Details',
      render: (text, record) => {
        const storm_details = record.storm_details || {};
        const { storm_type, storm_severity, date_of_storm } = storm_details;
        const { color, bg } = getSeverityColor(storm_severity || "N/A");
    
        return (
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              justifyContent: 'flex-start', 
              flexDirection: 'column', 
              width: '100%', 
            }}
          >
            {storm_type ? (
              <div>
            {(storm_type === "snow" || 
              storm_type === "Snow Showers" || 
              storm_type === "Isolated Snow Showers" ||
              storm_type === "Snow" || 
              storm_type === "Chance of Snow" || 
              storm_type === "Light Snow" ||
              storm_type === "Chance of Sleet/Snow" ||
              storm_type === "Scattered Snow Showers") ? (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  flexDirection: 'row', 
                  gap: '7px' 
                }}>
                  <div style={{ 
                    backgroundColor: bg,
                    height: '30px', 
                    width: '30px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    border: `1px solid ${color}`, 
                    borderRadius: '4px', 
                    color: color 
                  }}>
                    <IconSnowflake size={18} stroke={2} />
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    justifyContent: 'flex-start', 
                    flexDirection: 'column' 
                  }}>
                    <span style={{ fontWeight: '600', fontSize: '16px' }}>
                      {storm_type === "snow" ? "Snow" : storm_type}
                    </span>
                    <span style={{ fontSize: '14px', marginTop: '2px' }}>
                      Severity: {storm_severity}
                    </span>
                    <span style={{ fontSize: '14px', marginTop: '2px' }}>
                      Date: {formatDate(date_of_storm)}
                    </span>
                    <span style={{ fontSize: '14px', marginTop: '2px' }}>
                      Time: {formatTime(date_of_storm)} - {formatTime(storm_details.date_of_storm_end)}
                    </span>
                  </div>
                </div>
            ) : (storm_type === "freezing_rain" || 
                 storm_type === "Wintry Mix" || 
                 storm_type === "Chance of Wintry Mix" || 
                 storm_type === "Fog" ||
                 storm_type === "Isolated Freezing Rain" ||
                 storm_type === "Chance of Freezing Rain") ? (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  flexDirection: 'row', 
                  gap: '7px' 
                }}>
                  <div style={{ 
                    backgroundColor: bg,
                    height: '30px', 
                    width: '30px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    border: `1px solid ${color}`, 
                    borderRadius: '4px', 
                    color: color 
                  }}>
                    <IconCloudRain size={18} stroke={2} />
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    justifyContent: 'flex-start', 
                    flexDirection: 'column' 
                  }}>
                    <span style={{ fontWeight: '600', fontSize: '16px' }}>
                      {storm_type === "freezing_rain" ? "Freezing Rain" : storm_type}
                    </span>
                    <span style={{ fontSize: '14px', marginTop: '2px' }}>
                      Severity: {storm_severity}
                    </span>
                    <span style={{ fontSize: '14px', marginTop: '2px' }}>
                      Date: {formatDate(date_of_storm)}
                    </span>
                    <span style={{ fontSize: '14px', marginTop: '2px' }}>
                      Time: {formatTime(date_of_storm)} - {formatTime(storm_details.date_of_storm_end)}
                    </span>
                  </div>
                </div>
            ) : storm_type === "salt" ? (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  flexDirection: 'row', 
                  gap: '7px' 
                }}>
                  <div style={{ 
                    backgroundColor: bg,
                    height: '30px', 
                    width: '30px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    border: `1px solid ${color}`, 
                    borderRadius: '4px', 
                    color: color 
                  }}>
                    <IconGrain size={18} stroke={2} />
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    justifyContent: 'flex-start', 
                    flexDirection: 'column' 
                  }}>
                    <span style={{ fontWeight: '600', fontSize: '16px' }}>
                      Salting
                    </span>
                    <span style={{ fontSize: '14px', marginTop: '2px' }}>
                      Severity: {storm_severity}
                    </span>
                    <span style={{ fontSize: '14px', marginTop: '2px' }}>
                      Date: {formatDate(date_of_storm)}
                    </span>
                    <span style={{ fontSize: '14px', marginTop: '2px' }}>
                      Time: {formatTime(date_of_storm)} - {formatTime(storm_details.date_of_storm_end)}
                    </span>
                  </div>
                </div>
            ) : (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                flexDirection: 'row', 
                gap: '7px' 
              }}>
                <div style={{ 
                  backgroundColor: '#FDEAEA', 
                  height: '30px', 
                  width: '30px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  border: '1px solid #FF4D4E', 
                  borderRadius: '4px', 
                  color: "#FF4D4E" 
                }}>
                  <IconCloudQuestion size={18} stroke={2} />
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  justifyContent: 'flex-start', 
                  flexDirection: 'column' 
                }}>
                  <span style={{ fontWeight: '600', fontSize: '16px' }}>
                    {storm_type}
                  </span>
                  <span style={{ fontSize: '14px', marginTop: '2px' }}>
                    Severity: {storm_severity}
                  </span>
                  <span style={{ fontSize: '14px', marginTop: '2px' }}>
                    Date: {formatDate(date_of_storm)}
                  </span>
                  <span style={{ fontSize: '14px', marginTop: '2px' }}>
                    Time: {formatTime(date_of_storm)} - {formatTime(storm_details.date_of_storm_end)}
                  </span>
                </div>
              </div>
            )}
              </div>
            ) : (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                flexDirection: 'row', 
                gap: '7px' 
              }}>
                <div style={{ 
                  backgroundColor: '#F5F5F5', 
                  height: '30px', 
                  width: '30px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  border: '1px solid #808080', 
                  borderRadius: '4px', 
                  color: "#808080" 
                }}>
                  <IconCloudQuestion size={18} stroke={2} />
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  justifyContent: 'flex-start', 
                  flexDirection: 'column' 
                }}>
                  <span style={{ fontWeight: '600', fontSize: '16px' }}>
                    No Storm Details
                  </span>
                  <span style={{ fontSize: '14px', marginTop: '2px' }}>
                    Severity: N/A
                  </span>
                  <span style={{ fontSize: '14px', marginTop: '2px' }}>
                    Date: N/A
                  </span>
                  <span style={{ fontSize: '14px', marginTop: '2px' }}>
                    Time: N/A
                  </span>
                </div>
              </div>
            )}
          </div>
        )
      }
    },
    {
      title: 'Site',
      render: (text, record) => {
        const { site_details } = record;
    
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              flexDirection: 'row',
              gap: '10px',
            }}
          >
            {/* Building Icon */}
            <div
              style={{
                height: '30px',
                width: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f0f2f5',
                borderRadius: '4px',
              }}
            >
              <IconBuildingFactory2 size={18} stroke={2} />
            </div>
    
            {/* Site Details */}
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                flexDirection: 'column',
              }}
            >
              {/* Site Name */}
              <span style={{ fontWeight: '600', fontSize: '16px' }}>
                {site_details.site_name || 'N/A'}
              </span>
    
              {/* Client */}
              <span style={{ fontSize: '14px', marginTop: '2px' }}>
                {site_details.client || 'N/A'}
              </span>
    
              {/* Region */}
              <span style={{ fontSize: '14px', marginTop: '2px' }}>
                Region: {site_details.region || 'N/A'}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Staff',
      render: (text, record) => {
        const { staff_details } = record;
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '5px',
            }}
          >
            {/* Staff List */}
            {staff_details && staff_details.length > 0 ? (
              staff_details.map((staff, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: '10px',
                  }}
                >
                  {/* Icon */}
                  <div
                    style={{
                      height: '30px',
                      width: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f0f2f5',
                      borderRadius: '4px',
                    }}
                  >
                    <IconUser size={18} stroke={2} />
                  </div>
    
                  {/* Staff Details */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <span style={{ fontWeight: '600', fontSize: '14px' }}>
                      {staff.staff_name || 'N/A'}
                    </span>
                    <span style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                      {staff.staff_position || 'N/A'}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <span style={{ fontSize: '14px' }}> No Staff Assigned</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Vehicle',
      render: (text, record) => {
        const { vehicle_details } = record;
    
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              flexDirection: 'row',
              gap: '10px',
            }}
          >
            {/* Tractor Icon */}
            <div
              style={{
                height: '30px',
                width: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f0f2f5',
                borderRadius: '4px',
              }}
            >
              <IconTractor size={18} stroke={2} />
            </div>
    
            {/* Vehicle Details */}
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                flexDirection: 'column',
              }}
            >
              {/* Vehicle Name */}
              <span style={{ fontWeight: '600', fontSize: '16px' }}>
                {vehicle_details.vehicle_name || 'No Vehicle Assigned'}
              </span>
    
              {/* Vehicle Make */}
              <span style={{ fontSize: '14px', marginTop: '2px' }}>
                Make: {vehicle_details.vehicle_make || 'N/A'}
              </span>
    
              {/* Vehicle Model */}
              <span style={{ fontSize: '14px', marginTop: '2px' }}>
                Model: {vehicle_details.vehicle_model || 'N/A'}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width:175,
      render: (record) => (
        <div>
          <Button
            type="primary"
            onClick={() => showEditModal(record)}
            icon={<EditOutlined />}
            style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e' }}
          >
          Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this job?"
            onConfirm={() => handleDelete(record.job_id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="danger"
              icon={<DeleteOutlined />}
              className='delete-button'
            >
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleEditSubmit = async (values) => {
    try {
      const isWalkwayJob = values.type?.toLowerCase().trim() === 'walkway';
      


      const updatedJob = {
        storm: values.storm_id || null,
        site: values.site_id || null,
        staff: values.staff_ids || [],
        vehicle: values.vehicle_id || null,
        type: values.type,
        job_start_date: values.job_start_date ? values.job_start_date.tz('UTC').format() : null,
        job_end_date: values.job_end_date ? values.job_end_date.tz('UTC').format() : null
      };

      // only include crew_size in the payload if it's a walkway job
      if (isWalkwayJob) {
        updatedJob.crew_size = values.crew_size || null;
      }

      // log the payload we're sending
      console.log('Update payload:', updatedJob);
      console.log('Job ID:', editingJob.job_id);

      const response = await api.patch(`/debrief/jobs/${editingJob.job_id}/`, updatedJob);
      
      console.log('Update response:', response.data);

      message.success("Job updated successfully");
      setIsEditModalVisible(false);
      fetchJobs(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error("Failed to update job. Error details:", error);
      console.error("Error response:", error.response?.data);
      message.error(`Failed to update job: ${error.response?.data?.detail || error.message}`);
    }
  };

  const showEditModal = (job) => {
    console.log('Opening edit modal for job:', job);
    setEditingJob(job);
    setCurrentJobType(job.type);

    // pre-populate the form with the current job details
    const formValues = {
      type: job.type,
      storm_id: job.storm,
      site_id: job.site,
      staff_ids: job.staff_details ? job.staff_details.map((staff) => staff.staff_id) : [],
      vehicle_id: job.vehicle,
      job_start_date: job.job_start_date ? dayjs(job.job_start_date).tz('America/New_York') : null,
      job_end_date: job.job_end_date ? dayjs(job.job_end_date).tz('America/New_York') : null,
      crew_size: job.type?.toLowerCase().trim() === 'walkway' ? (job.crew_size || undefined) : undefined
    };

    console.log('Setting form values:', formValues);
    editForm.setFieldsValue(formValues);
    setIsEditModalVisible(true);
  };
  
  const handleEditCancel = () => {
    setIsEditModalVisible(false);
    setEditingJob(null);
    editForm.resetFields();
  };

  const handleJobTypeChange = (value) => {
    const newType = value?.trim() || "";
    setCurrentJobType(newType);
    if (newType.toLowerCase() !== 'walkway') {
      editForm.setFieldValue('crew_size', undefined);
    }
  };

  const handleDelete = async (jobId) => {
    try {
      await api.delete(`/debrief/jobs/${jobId}/`);
      message.success('Job deleted successfully');
      fetchJobs(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error('Failed to delete job:', error);
      message.error('Failed to delete job');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Edit modal */}
      <Modal
        title="Edit Job"
        open={isEditModalVisible}
        onCancel={handleEditCancel}
        footer={null}
      >
        <Form form={editForm} onFinish={handleEditSubmit} layout="vertical">
          <Form.Item name="type" label="Job Type" rules={[{ required: true, message: "Please enter the job type" }]}>
            <Input placeholder="Enter Job Type" onChange={(e) => handleJobTypeChange(e.target.value)} />
          </Form.Item>

          {/* Storm Selection */}
          <Form.Item name="storm_id" label="Storm" rules={[{ required: true, message: "Please select a storm" }]}>
            <Select
              showSearch
              placeholder="Search and select a storm"
              optionFilterProp="children"
              style={{ width: '100%' }}
            >
              {storms.map((storm) => (
                <Select.Option key={storm.storm_id} value={storm.storm_id}>
                  {`${capitalize(storm.type)} - ${dayjs(storm.date_of_storm).format('YYYY/MM/DD')} to ${dayjs(storm.date_of_storm_end).format('YYYY/MM/DD')} - ${storm.severity}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* Site Selection */}
          <Form.Item name="site_id" label="Site" rules={[{ required: true, message: "Please select a site" }]}>
            <Select
              showSearch
              placeholder="Search and select a site"
              optionFilterProp="children"
              style={{ width: '100%' }}
            >
              {sites.map((site) => (
                <Select.Option key={site.site_id} value={site.site_id}>
                  {site.site_name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* Staff Selection */}
          <Form.Item
            name="staff_ids"
            label="Staff"
            rules={[{ required: true, message: "Please select at least one staff member" }]}
          >
            <Select
              mode="multiple" // Allow selecting multiple options
              showSearch
              placeholder="Search and select staff members"
              optionFilterProp="children"
              style={{ width: '100%' }}
            >
              {staff.map((person) => (
                <Select.Option key={person.staff_id} value={person.staff_id}>
                  {person.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* Vehicle Selection */}
          <Form.Item name="vehicle_id" label="Vehicle" rules={[{ required: true, message: "Please select a vehicle" }]}>
            <Select
              showSearch
              placeholder="Search and select a vehicle"
              optionFilterProp="children"
              style={{ width: '100%' }}
            >
              {vehicles.map((vehicle) => (
                <Select.Option key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                  {vehicle.vehicle_name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* Job Start Date */}
          <Form.Item
            name="job_start_date"
            label="Job Start Date"
            rules={[{ required: true, message: "Please select the start date" }]}
          >
            <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
          </Form.Item>

          {/* Job End Date */}
          <Form.Item
            name="job_end_date"
            label="Job End Date"
            rules={[{ required: true, message: "Please select the end date" }]}
          >
            <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
          </Form.Item>

          {/* Conditionally render crew size field */}
          {currentJobType.toLowerCase().trim() === 'walkway' && (
            <Form.Item 
              name="crew_size" 
              label="Crew Size" 
              rules={[
                { required: true, message: "Please enter the crew size" },
                { type: 'number', min: 1, message: "Crew size must be at least 1" },
                { 
                  validator: (_, value) => {
                    if (!value || Number.isNaN(value)) {
                      return Promise.reject('Please enter a valid number');
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <InputNumber 
                min={1} 
                placeholder="Enter crew size"
                style={{ width: '100%' }}
              />
            </Form.Item>
          )}

          {/* Submit Button */}
          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e' }}>
              Update Job
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Modal */}
      <Modal
        title="Add New Job"
        open={isAddModalVisible}
        onCancel={handleAddCancel}
        footer={null}
      >
        <Form form={addForm} onFinish={handleAddSubmit} layout="vertical">
          <Form.Item name="type" label="Job Type" rules={[{ required: true, message: "Please enter the job type" }]}>
            <Input placeholder="Enter Job Type" />
          </Form.Item>
          <Form.Item name="storm_id" label="Storm" rules={[{ required: true, message: "Please select a storm" }]}>
            <Select
              showSearch
              placeholder="Search and select a storm"
              optionFilterProp="children"
              style={{ width: '100%' }}
            >
              {storms.map((storm) => (
                <Select.Option key={storm.storm_id} value={storm.storm_id}>
                  {`${capitalize(storm.type)} - ${dayjs(storm.date_of_storm).format('YYYY/MM/DD')} to ${dayjs(storm.date_of_storm_end).format('YYYY/MM/DD')} - ${storm.severity}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="site_id" label="Site" rules={[{ required: true, message: "Please select a site" }]}>
            <Select
              showSearch
              placeholder="Search and select a site"
              optionFilterProp="children"
              style={{ width: '100%' }}
            >
              {sites.map((site) => (
                <Select.Option key={site.site_id} value={site.site_id}>
                  {site.site_name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="staff_ids"
            label="Staff"
            rules={[{ required: true, message: "Please select at least one staff member" }]}
          >
            <Select
              mode="multiple" // Allow selecting multiple options
              showSearch
              placeholder="Search and select staff members"
              optionFilterProp="children"
              style={{ width: '100%' }}
            >
              {staff.map((person) => (
                <Select.Option key={person.staff_id} value={person.staff_id}>
                  {person.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="vehicle_id" label="Vehicle" rules={[{ required: true, message: "Please select a vehicle" }]}>
            <Select
              showSearch
              placeholder="Search and select a vehicle"
              optionFilterProp="children"
              style={{ width: '100%' }}
            >
              {vehicles.map((vehicle) => (
                <Select.Option key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                  {vehicle.vehicle_name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="job_start_date" label="Job Start Date" rules={[{ required: true, message: "Please select the start date" }]}>
            <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="job_end_date" label="Job End Date" rules={[{ required: true, message: "Please select the end date" }]}>
            <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e' }}>Add Job</Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Sync Jobs Modal */}
      <SyncJobsModal
        open={isSyncModalOpen}
        onClose={handleSyncModalClose}
        storms={storms}
      />

      {/* Jobs Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ marginLeft: 30 }}>Jobs</h2>
      </div>
  
      {/* Filters and actions */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '16px',
        backgroundColor: '#f9f9f9',
        padding: '16px',
        borderRadius: '8px',
        margin: '0 30px 16px 30px'
      }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Job Type Filter */}
          <div style={{ minWidth: '150px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>Job Type</label>
            <Select
              value={jobTypeFilter}
              onChange={setJobTypeFilter}
              style={{ width: '100%' }}
              placeholder="Filter by job type"
            >
              <Option value="All">All Jobs</Option>
              {jobTypes.map(jobType => (
                <Option key={jobType} value={jobType}>{jobType}</Option>
              ))}
            </Select>
          </div>
          
          {/* Storm Type Filter */}
          <div style={{ minWidth: '150px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>Storm Type</label>
            <Select
              value={stormTypeFilter}
              onChange={setStormTypeFilter}
              style={{ width: '100%' }}
              placeholder="Filter by storm type"
            >
              <Option value="">All Storm Types</Option>
              <Option value="snow">Snow</Option>
              <Option value="freezing_rain">Freezing Rain</Option>
              <Option value="salt">Salt</Option>
            </Select>
          </div>
          
          {/* Site Name Filter */}
          <div style={{ minWidth: '180px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>Site</label>
            <Select
              mode="multiple"
              placeholder="Search by site name"
              value={siteNameFilters}
              onChange={setSiteNameFilters}
              style={{ width: '100%' }}
              showSearch
              allowClear
              optionFilterProp="children"
              filterOption={(input, option) => 
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {sites.map((site) => (
                <Option key={site.site_name} value={site.site_name}>
                  {site.site_name}
                </Option>
              ))}
            </Select>
          </div>
          
          {/* Staff Name Filter */}
          <div style={{ minWidth: '180px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>Staff</label>
            <Input
              placeholder="Filter by staff name"
              value={staffNameFilter}
              onChange={e => setStaffNameFilter(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
            />
          </div>
          
          {/* Vehicle Name Filter */}
          <div style={{ minWidth: '180px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>Vehicle</label>
            <Input
              placeholder="Filter by vehicle name"
              value={vehicleNameFilter}
              onChange={e => setVehicleNameFilter(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
            />
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={showAddModal}
            style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e' }}
          >
            Add Job
          </Button>
          <Button
            type="default"
            icon={<SyncOutlined />}
            onClick={() => setIsSyncModalOpen(true)}
          >
            Sync Jobs
          </Button>
        </div>
      </div>
  
      {/* Jobs Table */}
      <div style={{ margin: '0 30px 30px 30px', overflow: 'auto' }}>
        <Table
          dataSource={jobs}
          columns={columns.map(col => ({
            ...col,
            title: <span style={{ fontWeight: '600' }}>{col.title}</span>
          }))}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} jobs`,
            onChange: (page, pageSize) => {
              fetchJobs(page, pageSize);
            }
          }}
          scroll={{ x: 'max-content' }}
          loading={loading}
          rowKey="job_id"
          bordered
          style={{ 
            borderRadius: '8px', 
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
          }}
        />
      </div>

    </div>
  );
};

export default DebriefJobs;